import { Add, Remove } from "@material-ui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useStateContext } from "../../context/StateContext";
import { mobileView, tabView } from "../../global/responsive";

// Styled Components
const Container = styled.div``;

const Wrapper = styled.div`
  padding: 50px !important;
  display: flex;
  align-items: center;
  gap: 20px;
  ${tabView({
    padding: "10px !important",
    flexDirection: "column !important",
  })}
`;

const ImgContainer = styled.div`
  flex: 1 !important;
`;

const Image = styled.img`
  width: 100%;
  height: 90vh;
  object-fit: cover !important;
  ${mobileView({ height: "40vh" })}
`;

const InfoContainer = styled.div`
  flex: 1;
  padding: 0px 50px !important;
  ${tabView({ padding: "10px !important" })};
`;

const Title = styled.h1`
  font-weight: 600;
`;

const Desc = styled.p`
  margin: 20px 0px !important;
`;

const Price = styled.span`
  font-weight: 100;
  font-size: 40px;
`;

const FilterContainer = styled.div`
  width: 50% !important;
  margin: 30px 0px !important;
  display: flex;
  justify-content: space-between !important;
  flex-wrap: wrap;
  gap: 10px;
  ${tabView({ width: "100% !important" })}
`;

const Filter = styled.div`
  display: flex;
  align-items: center;
`;

const FilterTitle = styled.span`
  font-size: 20px;
  font-weight: 200;
`;

const FilterColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${(props) => props.color};
  margin: 0px 5px !important;
  cursor: pointer;
  &.clicked {
    border: 2px solid #b8a06a !important;
  }
`;

const FilterSize = styled.select`
  margin-left: 10px !important;
  padding: 5px !important;
`;

const FilterSizeOption = styled.option``;

const AddContainer = styled.div`
  width: 50%;
  display: flex;
  gap: 15px;
  align-items: center;
  justify-content: space-between;
  ${tabView({ width: "100%" })}
`;

const AmountContainer = styled.div`
  display: flex;
  align-items: center;
  font-weight: 700;
  gap: 4px;
  ${tabView({ gap: "9px" })}
`;

const Amount = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 10px;
  border: 1px solid #b8a06a;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0px 5px !important;
`;

const Button = styled.button`
  padding: 15px !important;
  border: 2px solid #b8a06a;
  background-color: white;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background-color: #f8f4f4;
  }
`;

// Color and Product Type Constants
const COLOR_PRESETS = {
  default: [
    { name: "Navy Blue", hex: "#000080" },
    { name: "Brown", hex: "brown" },
    { name: "Army Green", hex: "#4b5320" },
    { name: "Dark Gray", hex: "darkgray" },
    { name: "Light Gray", hex: "lightgray" },
    { name: "Royal Blue", hex: "#3f66da" },
    { name: "Black", hex: "#000" },
    { name: "Cream", hex: "#fffdd0" },
  ],
  "bucket-hat": [
    { name: "Black", hex: "#000" },
    { name: "White", hex: "whitesmoke" },
  ],
  bag: [
    { name: "Black", hex: "#000" },
    { name: "Blue", hex: "#000080" },
  ],
};

const PRODUCT_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];
const TROUSER_LENGTHS = [
  "35",
  "36",
  "37",
  "38",
  "39",
  "40",
  "41",
  "42",
  "43",
  "44",
  "45",
];
const POLO_TYPES = ["Button", "Zipper"];

// Image carousel interval in milliseconds
const CAROUSEL_INTERVAL = 5500;

const Productdetail = () => {
  const { productName } = useParams();
  const [color, setColor] = useState("black");
  const [size, setSize] = useState("XS");
  const [type, setType] = useState("Button");
  const [trouserLen, setTrouserLen] = useState("35");
  const [colorName, setColorName] = useState("");
  const [imageIndex, setImageIndex] = useState(0);

  const { products, qty, increaseQty, decreaseQty, onAddToCart } =
    useStateContext();

  // Find product and memoize to prevent unnecessary recalculations
  const product = useMemo(
    () => products.find((item) => item.productName === productName),
    [products, productName]
  );

  const productImageCount = product?.productImages?.length || 0;
  const maxImageIndex = productImageCount - 1;

  // Get colors based on product type
  const availableColors = useMemo(
    () =>
      product
        ? COLOR_PRESETS[product.productType] || COLOR_PRESETS.default
        : COLOR_PRESETS.default,
    [product]
  );

  // Auto-rotate carousel
  useEffect(() => {
    if (productImageCount <= 1) return;

    const interval = setInterval(() => {
      setImageIndex((prevIndex) =>
        prevIndex >= maxImageIndex ? 0 : prevIndex + 1
      );
    }, CAROUSEL_INTERVAL);

    return () => clearInterval(interval);
  }, [productImageCount, maxImageIndex]);

  // Memoized color selection handler
  const handleColorSelect = useCallback((selectedColor) => {
    setColor(selectedColor.hex);
    setColorName(selectedColor.name);
    toast.success(`${selectedColor.name} selected successfully`);
  }, []);

  // Memoized size selection handler
  const handleSizeChange = useCallback((e) => {
    setSize(e.target.value);
    toast.success(`${e.target.value} selected successfully`);
  }, []);

  // Memoized type selection handler
  const handleTypeChange = useCallback((e) => {
    setType(e.target.value);
  }, []);

  // Memoized trouser length selection handler
  const handleTrouserLenChange = useCallback((e) => {
    setTrouserLen(e.target.value);
  }, []);

  // Memoized add to cart handler
  const handleAddToCart = useCallback(() => {
    if (!product) return;
    onAddToCart(
      product,
      qty,
      color,
      colorName,
      size,
      product.productType === "joggers-and-sweatpant-and-cargos"
        ? trouserLen
        : null,
      product.productType === "polo" ? type : null
    );
  }, [product, qty, color, colorName, size, trouserLen, type, onAddToCart]);

  // Guard against missing product - render after all hooks
  if (!product) {
    return (
      <Container>
        <Wrapper>
          <p>Product not found</p>
        </Wrapper>
      </Container>
    );
  }

  return (
    <Container>
      <Wrapper>
        <ImgContainer>
          <Image
            src={product.productImages?.[imageIndex]}
            alt={product.productName}
          />
        </ImgContainer>
        <InfoContainer>
          <Title>{product.productName}</Title>
          <Desc>{product.description}</Desc>
          <Price>₦{product.productPrice}</Price>
          <FilterContainer>
            <Filter>
              <FilterTitle>Color:</FilterTitle>
              {availableColors.map((col) => (
                <FilterColor
                  key={col.name}
                  color={col.hex}
                  onClick={() => handleColorSelect(col)}
                  title={col.name}
                />
              ))}
            </Filter>

            <Filter>
              <FilterTitle>Size:</FilterTitle>
              <FilterSize onChange={handleSizeChange} value={size}>
                {PRODUCT_SIZES.map((sizeOption) => (
                  <FilterSizeOption key={sizeOption} value={sizeOption}>
                    {sizeOption}
                  </FilterSizeOption>
                ))}
              </FilterSize>
            </Filter>

            {product.productType === "polo" && (
              <Filter>
                <FilterTitle>Type:</FilterTitle>
                <FilterSize onChange={handleTypeChange} value={type}>
                  {POLO_TYPES.map((poloType) => (
                    <FilterSizeOption key={poloType} value={poloType}>
                      {poloType}
                    </FilterSizeOption>
                  ))}
                </FilterSize>
              </Filter>
            )}

            {product.productType === "joggers-and-sweatpant-and-cargos" && (
              <Filter>
                <FilterTitle>Length:</FilterTitle>
                <FilterSize
                  onChange={handleTrouserLenChange}
                  value={trouserLen}
                >
                  {TROUSER_LENGTHS.map((length) => (
                    <FilterSizeOption key={length} value={length}>
                      {length}
                    </FilterSizeOption>
                  ))}
                </FilterSize>
              </Filter>
            )}
          </FilterContainer>

          <AddContainer>
            <AmountContainer>
              <Remove onClick={decreaseQty} style={{ cursor: "pointer" }} />
              <Amount>{qty}</Amount>
              <Add onClick={increaseQty} style={{ cursor: "pointer" }} />
            </AmountContainer>
            <Button onClick={handleAddToCart}>ADD TO CART</Button>
          </AddContainer>
        </InfoContainer>
      </Wrapper>
    </Container>
  );
};

export default Productdetail;
